/**
 * DateRangePicker Component
 * Date range selector with common presets (7d, 30d, 90d, custom)
 */

import { useState } from "react";

export interface DateRange {
  start: Date;
  end: Date;
}
interface DateRangePickerProps {
  value: DateRange;
  onChange: (range: DateRange) => void;
  className?: string;
}
type Preset = "7d" | "30d" | "90d" | "custom";

export function DateRangePicker({
  value,
  onChange,
  className = "",
}: DateRangePickerProps) {
  const [selectedPreset, setSelectedPreset] = useState<Preset>("30d");

  const handlePresetChange = (preset: Preset) => {
    setSelectedPreset(preset);
    if (preset === "custom") return;
    const end = new Date();
    const start = new Date();
    if (preset === "7d") start.setDate(end.getDate() - 7);
    else if (preset === "30d") start.setDate(end.getDate() - 30);
    else if (preset === "90d") start.setDate(end.getDate() - 90);
    onChange({ start, end });
  };

  const formatDate = (date: Date) => date.toISOString().split("T")[0];

  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      <div className="flex rounded-md border border-border-default bg-bg-secondary">
        {(["7d", "30d", "90d", "custom"] as Preset[]).map((preset) => (
          <button
            key={preset}
            onClick={() => handlePresetChange(preset)}
            className={`px-3 py-2 text-sm font-medium transition-colors first:rounded-l-md last:rounded-r-md ${selectedPreset === preset ? "bg-primary-500 text-white" : "text-text-secondary hover:text-text-primary hover:bg-bg-tertiary"}`}
            type="button"
            aria-pressed={selectedPreset === preset}
          >
            {preset === "7d"
              ? "Last 7 days"
              : preset === "30d"
                ? "Last 30 days"
                : preset === "90d"
                  ? "Last 90 days"
                  : "Custom"}
          </button>
        ))}
      </div>
      {selectedPreset === "custom" && (
        <div className="flex items-center gap-2">
          <label htmlFor="start-date" className="sr-only">
            Start date
          </label>
          <input
            id="start-date"
            type="date"
            value={formatDate(value.start)}
            onChange={(e) =>
              onChange({ ...value, start: new Date(e.target.value) })
            }
            className="rounded-md border border-border-default bg-bg-secondary px-3 py-2 text-sm text-text-primary focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
          <span className="text-text-secondary">to</span>
          <label htmlFor="end-date" className="sr-only">
            End date
          </label>
          <input
            id="end-date"
            type="date"
            value={formatDate(value.end)}
            onChange={(e) =>
              onChange({ ...value, end: new Date(e.target.value) })
            }
            className="rounded-md border border-border-default bg-bg-secondary px-3 py-2 text-sm text-text-primary focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      )}
    </div>
  );
}
